import { NavigationType, type DataRouter } from "react-router";

// Tracks whether the current history entry was reached by navigating within the app,
// so BackLink only calls navigate(-1) when that stays in DXP-UI and otherwise uses
// its fallback route and label

const STORAGE_KEY = "inAppHistoryDepthByKey";

const isDepthRecord = (value: unknown): value is Record<string, number> =>
  typeof value === "object" &&
  value !== null &&
  Object.values(value).every((depth) => typeof depth === "number");

const readStoredDepths = (): Map<string, number> => {
  try {
    const parsed: unknown = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY) ?? "{}"
    );
    return new Map(isDepthRecord(parsed) ? Object.entries(parsed) : []);
  } catch {
    return new Map();
  }
};

const depthByKey = readStoredDepths();

const persistDepths = () => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Object.fromEntries(depthByKey))
    );
  } catch {
    // Storage can be unavailable (e.g. private mode); tracking still works in memory.
  }
};

/**
 * Records how many in-app pushes deep each history entry is, keyed by React
 * Router's location key. Independent of `window.history.state`, which other
 * code may overwrite.
 */
export function trackInAppHistory(router: DataRouter): () => void {
  let currentKey = router.state.location.key;

  return router.subscribe(({ location, historyAction }) => {
    if (location.key === currentKey) return;

    const previousDepth = depthByKey.get(currentKey) ?? 0;
    if (historyAction === NavigationType.Push) {
      depthByKey.set(location.key, previousDepth + 1);
    } else if (historyAction === NavigationType.Replace) {
      depthByKey.delete(currentKey);
      depthByKey.set(location.key, previousDepth);
    }
    currentKey = location.key;
    persistDepths();
  });
}

export const hasInAppHistory = (locationKey: string): boolean =>
  (depthByKey.get(locationKey) ?? 0) > 0;
